/**
 * Text / HTML Helpers
 *
 * Pure stateless text processing utilities. No store access.
 *
 * Migrated from: src/mixins/global.js
 */
import DOMPurify from 'dompurify'

// DOMPurify configuration used for all chat HTML.
// - Only the tags and attributes needed to render chat messages are allowed.
// - href/src are restricted to http(s) only via ALLOW_UNKNOWN_PROTOCOLS: false
//   and the ADD_ATTR target+rel pair for anchors.
// - style is deliberately excluded to prevent CSS-based data exfiltration.
const PURIFY_CONFIG: DOMPurify.Config = {
  ALLOWED_TAGS: ['a', 'b', 'i', 'em', 'strong', 'br', 'p', 'span', 'img'],
  ALLOWED_ATTR: ['href', 'src', 'class', 'target', 'rel', 'alt'],
  ALLOW_DATA_ATTR: false,
  ALLOW_UNKNOWN_PROTOCOLS: false,
  // Restrict all URI-bearing attributes (href, src, …) to http(s) only.
  // This explicitly blocks data: and blob: URIs on img[src], which DOMPurify
  // would otherwise permit by default even with ALLOW_UNKNOWN_PROTOCOLS: false.
  ALLOWED_URI_REGEXP: /^https?:/i,
  // Force safe link attributes so auto-linked anchors always open safely.
  ADD_ATTR: ['target', 'rel'],
}

/**
 * Sanitises a raw string using DOMPurify so it is safe for v-html rendering.
 * Only an explicit allowlist of tags/attributes survives; dangerous protocols
 * (javascript:, data:text/html, etc.) and inline styles are stripped.
 */
export function genericSanitizeHtml(input: string | undefined | null): string | undefined | null {
  if (!input || input === 'None') return input
  return DOMPurify.sanitize(input, PURIFY_CONFIG)
}

/**
 * Formats a chat message for safe HTML rendering:
 * - Escapes raw `<` / `>` so plain-text messages are not interpreted as HTML
 * - Converts https?:// URLs to clickable anchor tags with safe attributes
 * - Converts line-break markers and newlines to `<br>`
 * - Runs the result through DOMPurify to strip any remaining unsafe markup
 */
export function renderNormalChat(input: string | undefined | null): string | undefined | null {
  if (!input || input === 'None') return input

  // Escape any literal angle brackets in the raw text before adding our own tags.
  let output = input.replace(/</g, '&lt;').replace(/>/g, '&gt;')

  output = output.replace(/(https?:\/\/[^\s<]+)/g, (match) => {
    const safeUrl = match.replace(/&/g, '&amp;').replace(/"/g, '&quot;')
    return `<a href="${safeUrl}" class="reply_msg" target="_blank" rel="noopener noreferrer">${safeUrl}</a>`
  })

  output = output.replace(/:::::/g, '<br>').replace(/(?:\r\n|\r|\n)/g, '<br>')

  // Final DOMPurify pass — sanitises the HTML we just constructed and removes
  // any unexpected markup that may have survived the plain-text escaping above.
  return DOMPurify.sanitize(output, PURIFY_CONFIG)
}
